import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { InterviewRequestView, RequestStatus, RequestType, RequestView, WWCCRequestView } from "../../firestore/Types/RequestSchema";
import { approveRequest, getRequestById, rejectRequest } from "../../firestore/Functions/requestFunction";
import { LoggedInLayoutSelector } from "../../Layouts/LoggedInLayoutSelector";
import { VerifyAdmin } from "./VerifyAdmin";
import { ViewAccount } from "../../components/Account/ViewAccount";
import { AccountView } from "../../firestore/Types/AccountSchema";
import { getAccountById } from "../../firestore/Functions/accountFunctions";
import { StatusTag, TagSize } from "../../components/Account/StatusTag";
import { getStatusColor, getStatusText } from "../../Utils/RequestColorAndTextSelector";
import { WWCCRequest } from "../../components/AdminRequests/WWCCRequest";
import { InterviewRequest } from "../../components/AdminRequests/InterviewRequest";
import { PermissionView, Roles } from "../../firestore/Types/PermissionSchema";
import { getPermissionById } from "../../firestore/Functions/permissionFunctions";

export function ViewAdminRequest() {
    const [request, setRequest] = useState<RequestView>();
    const [account, setAccount] = useState<AccountView>({} as AccountView);
    const [permission, setPermission] = useState<PermissionView>();
    const [loading, setLoading] = useState(false);
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const requestId = searchParams.get("requestId");

    const setupPage = useCallback(async (requestId: string | null) => {
        if (requestId === null) {
            navigate("/admin-requests");
            return;
        }

        const newReuqest = await getRequestById(requestId).catch(() => {});
        const newAccount = await getAccountById(newReuqest?.UserId ?? "").catch(() => {});
        const newPermission = await getPermissionById(newReuqest?.UserId ?? "").catch(() => {});

        if (newReuqest === undefined) {
            navigate("/admin-requests");
            return;
        }

        if (newAccount !== undefined) {
            setAccount(newAccount);
        }

        if (newPermission !== undefined) {
            setPermission(newPermission);
        }

        setRequest(newReuqest);
        
    }, [navigate]);

    useEffect(() => {
        setupPage(requestId);
    }, [requestId, setupPage]);

    const handleApprove = async () => {
        setLoading(true);
        if (request === undefined) {
            return;
        }
        await approveRequest(request.Id);
        await setupPage(requestId);
        setLoading(false);
    }

    const handleReject = async () => {
        setLoading(true);
        if (request === undefined) {
            return;
        }
        await rejectRequest(request.Id);
        await setupPage(requestId);
        setLoading(false);
    }

    return (
        <LoggedInLayoutSelector>
            <VerifyAdmin />
            <div className="main-bg">
                <div className="gap-4 grid grid-cols-10 grid-rows-2">
                    <div className="col-span-2 max-lg:col-span-3 max-md:col-span-10 row-span-2">
                        <ViewAccount account={account}></ViewAccount>
                    </div>
                    <div className="gap-4 col-span-7 grid grid-cols-1 max-lg:col-span-6 2xl:grid-cols-2">
                        <div className="section-box p-4">
                            <h3 className="section-title">{"User type: " + getPermissionName(permission?? {} as PermissionView)}</h3>
                            <br/>
                            <h3 className="section-title">{request?.Date.toDate().toLocaleString()}</h3>
                            <br/>
                            <StatusTag tagSize={TagSize.Medium} statusText={getStatusText(request?.Status ?? RequestStatus.None)} statusTagColor={getStatusColor(request?.Status ?? RequestStatus.None)}></StatusTag>
                            <br/>
                            {request?.Status === RequestStatus.Pending &&
                            <div className="flex w-full items-center justify-center gap-4">
                                <button className="primary-button w-[30%] justify-center" onClick={handleApprove}> Approve </button>
                                <button className="primary-button w-[30%] justify-center" onClick={handleReject}> Reject </button>
                            </div>}
                            {loading && <div>Loading...</div>}
                        </div>
                        {request?.Type === RequestType.WWCC && <WWCCRequest request={request as WWCCRequestView}/>}
                        {request?.Type === RequestType.Interview && <InterviewRequest request={request as InterviewRequestView}/>}
                    </div>
                </div>
            </div>
        </LoggedInLayoutSelector>
    );
}

function getPermissionName(permission: PermissionView) {
    switch (permission.Role) {
        case Roles.Admin:
            return "Admin";
        case Roles.Tutor:
            return "Tutor";
        case Roles.User:
            return "User";
        default:
            return "Unknown";
    }
}